import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, formatMoney, strengthMeta, formatDate, reconciliationMeta, isStaleReviewError, staleReviewPayload } from "../lib/api";
import { toast } from "sonner";
import { Check, X, Quote, ShieldQuestion, CircleDollarSign, AlertCircle, Copy, Sparkles, RefreshCcw } from "lucide-react";

const Badge = ({ meta }) => (
  <span
    className="inline-flex items-center px-2 py-[3px] font-mono text-[10px] uppercase tracking-wider rounded-sm"
    style={{ backgroundColor: meta.bg, color: meta.fg }}
  >
    {meta.label}
  </span>
);

export const ReviewDialog = ({ open, onOpenChange, analysis, onAccepted, onFulfilled }) => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [duplicateResolutions, setDuplicateResolutions] = useState({}); // candidate_id -> keep_existing|replace|keep_both
  const [saving, setSaving] = useState(false);
  const [reconciliationState, setReconciliationState] = useState({}); // rid -> { staleCurrent?, refreshedVersion?, applied? }
  const [reconciliationOverrides, setReconciliationOverrides] = useState({}); // rid -> refreshed reconciliation match

  useEffect(() => {
    if (!analysis) return;
    const arr = (analysis.detected || []).map((c) => ({ ...c }));
    setItems(arr);
    const sel = {};
    arr.forEach((c) => (sel[c.id] = true));
    setSelected(sel);
    setDuplicateResolutions({});
    setReconciliationState({});
    setReconciliationOverrides({});
  }, [analysis]);

  const rejected = analysis?.rejected || [];
  const fulfillmentMatches = analysis?.fulfillment_matches || [];
  const duplicateCandidates = analysis?.duplicate_candidates || [];
  const rawReconciliationMatches = analysis?.reconciliation_matches || [];
  const reconciliationMatches = rawReconciliationMatches
    .map((m) => reconciliationOverrides[m.reconciliation_id] || m)
    .filter((m) => !reconciliationState[m.reconciliation_id]?.applied);
  const dupeByCand = Object.fromEntries(duplicateCandidates.map((d) => [d.candidate_id, d]));

  const refreshMatch = async (m) => {
    try {
      const fresh = await api.getCommitment(m.matched_commitment_id);
      const c = fresh.commitment;
      const updated = {
        ...m,
        existing_commitment: c,
        reviewed_commitment_version: c.version || 1,
        reviewed_status: c.status,
      };
      // If proposed date was date-based, keep it; but re-anchor previous_expected_completion to the fresh doc
      if (updated.proposed_changes) {
        updated.proposed_changes = {
          ...updated.proposed_changes,
          previous_expected_completion: c.expected_completion,
        };
      }
      setReconciliationOverrides((s) => ({ ...s, [m.reconciliation_id]: updated }));
      setReconciliationState((s) => ({ ...s, [m.reconciliation_id]: { ...(s[m.reconciliation_id] || {}), staleCurrent: null } }));
      toast.success("Refreshed — showing latest commitment state");
    } catch {
      toast.error("Could not refresh commitment");
    }
  };

  const applyReconciliation = async (m) => {
    try {
      await api.reconcile(m.matched_commitment_id, {
        reconciliation_type: m.reconciliation_type,
        evidence: m.full_evidence || m.evidence_quote,
        conversation_id: m.conversation_id,
        reconciliation_id: m.reconciliation_id,
        changes: m.proposed_changes || {},
        reviewed_commitment_version: m.reviewed_commitment_version,
      });
      toast.success(`${reconciliationMeta[m.reconciliation_type]?.label || "Applied"} — commitment updated`);
      setReconciliationState((s) => ({ ...s, [m.reconciliation_id]: { applied: true } }));
      const tid = analysis?.source_thread_id || analysis?.__sourceThreadId;
      if (tid) window.dispatchEvent(new CustomEvent("cl:threadAccepted", { detail: { sourceThreadId: tid } }));
      onFulfilled?.(m.matched_commitment_id);
      // Auto-close if this was the last lifecycle update AND there are no candidates or fulfilment matches
      const remainingLifecycle = rawReconciliationMatches.filter((r) =>
        r.reconciliation_id !== m.reconciliation_id && !reconciliationState[r.reconciliation_id]?.applied
      );
      if (remainingLifecycle.length === 0 && items.length === 0 && fulfillmentMatches.length === 0) {
        onOpenChange(false);
      }
    } catch (e) {
      if (isStaleReviewError(e)) {
        const payload = staleReviewPayload(e);
        setReconciliationState((s) => ({
          ...s,
          [m.reconciliation_id]: {
            ...(s[m.reconciliation_id] || {}),
            staleCurrent: payload?.current_state,
          },
        }));
        toast.error("This commitment changed after this review was created. Refresh to review its current state.");
      } else if (e?.response?.status === 422 && e?.response?.data?.detail?.error_code === "REVISION_MISSING_REPLACEMENT") {
        toast.error("Revision needs a proposed date, an approximate phrase, or an explicit withdrawal.");
      } else {
        toast.error("Failed to apply reconciliation");
      }
    }
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const setAll = (v) => {
    const next = {};
    items.forEach((c) => (next[c.id] = v));
    setSelected(next);
  };
  const editField = (id, field, value) =>
    setItems((arr) => arr.map((c) => (c.id === id ? { ...c, [field]: value } : c)));

  const confirmFulfillment = async (match) => {
    try {
      await api.fulfillWithEvidence(match.existing_commitment.id, {
        fulfilled_at: match.suggested_fulfilled_date,
        fulfillment_evidence: match.evidence_quote,
        fulfillment_conversation_id: match.candidate.conversation_id,
      });
      toast.success("Marked fulfilled — evidence attached");
      const tid = analysis?.source_thread_id || analysis?.__sourceThreadId;
      if (tid) {
        window.dispatchEvent(new CustomEvent("cl:threadAccepted", { detail: { sourceThreadId: tid } }));
      }
      onFulfilled?.(match.existing_commitment.id);
    } catch {
      toast.error("Failed to confirm fulfilment");
    }
  };

  const acceptSelected = async () => {
    const chosen = items.filter((c) => {
      if (!selected[c.id]) return false;
      const dupe = dupeByCand[c.id];
      if (dupe && duplicateResolutions[c.id] === "keep_existing") return false;
      return true;
    });
    if (chosen.length === 0) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      // Handle "replace existing" for duplicates
      for (const cand of chosen) {
        const dupe = dupeByCand[cand.id];
        if (dupe && duplicateResolutions[cand.id] === "replace") {
          await api.updateCommitment(dupe.existing_commitment.id, { status: "dismissed" });
        }
      }
      const res = await api.accept(chosen);
      toast.success(`${res.count} commitment${res.count === 1 ? "" : "s"} added to ledger.`);
      onAccepted?.(res.accepted || [], analysis?.source_thread_id || analysis?.__sourceThreadId);
      onOpenChange(false);
    } catch {
      toast.error("Failed to accept commitments");
    } finally {
      setSaving(false);
    }
  };

  const chosenCount = items.filter((c) => selected[c.id] && duplicateResolutions[c.id] !== "keep_existing").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="review-dialog"
        className="max-w-4xl max-h-[90vh] overflow-y-auto bg-ledger-surface border border-ledger-border p-0 gap-0"
      >
        <DialogHeader className="px-8 pt-7 pb-4 border-b border-ledger-border">
          <DialogTitle className="font-display text-3xl text-ledger-ink">
            Review detected commitments
          </DialogTitle>
          <DialogDescription className="text-sm text-ledger-sub" data-testid="review-summary">
            {items.length === 0 && fulfillmentMatches.length === 0 && reconciliationMatches.length > 0
              ? `${reconciliationMatches.length} lifecycle update${reconciliationMatches.length === 1 ? "" : "s"} detected. Nothing changes until you confirm.`
              : items.length === 0 && fulfillmentMatches.length === 0 && reconciliationMatches.length === 0
              ? "Nothing looked like a firm commitment in this conversation."
              : `${items.length} candidate${items.length === 1 ? "" : "s"} detected${fulfillmentMatches.length ? ` · ${fulfillmentMatches.length} possible fulfilment` : ""}${reconciliationMatches.length ? ` · ${reconciliationMatches.length} lifecycle update${reconciliationMatches.length === 1 ? "" : "s"}` : ""}${duplicateCandidates.length ? ` · ${duplicateCandidates.length} possible duplicate` : ""}. Nothing enters the ledger until you accept.`}
          </DialogDescription>
        </DialogHeader>

        {reconciliationMatches.length > 0 && (
          <div className="px-8 py-4 border-b border-ledger-border space-y-3">
            {reconciliationMatches.map((m) => {
              const meta = reconciliationMeta[m.reconciliation_type] || reconciliationMeta.fulfilled;
              const existing = m.existing_commitment;
              const rid = m.reconciliation_id;
              const pc = m.proposed_changes || {};
              const stale = reconciliationState[rid]?.staleCurrent;
              const isRevisionLike = m.reconciliation_type === "revised" || m.reconciliation_type === "delayed";
              const hasProposedField = isRevisionLike
                ? Boolean(pc.proposed_expected_completion || pc.revised_deadline_phrase || pc.withdrawn)
                : true;
              const missingRevisionReplacement = isRevisionLike && !hasProposedField;
              return (
                <div
                  key={rid}
                  data-testid={`reconciliation-match-${m.reconciliation_type}`}
                  className="rounded-sm p-4"
                  style={{ backgroundColor: meta.bg, border: `1px solid ${meta.border}` }}
                >
                  <div className="text-[11px] uppercase tracking-[0.18em] mb-2 flex items-center justify-between" style={{ color: meta.fg }}>
                    <span>{meta.label}</span>
                    <span className="font-mono text-[10px] text-ledger-muted">
                      version {m.reviewed_commitment_version || 1}
                    </span>
                  </div>
                  {stale && (
                    <div
                      data-testid={`reconciliation-stale-${m.reconciliation_type}`}
                      className="mb-3 bg-white border border-[#B91C1C] rounded-sm p-3"
                    >
                      <div className="text-[11px] uppercase tracking-[0.18em] text-[#B91C1C] mb-1">
                        Commitment changed since this review was created
                      </div>
                      <div className="text-[12px] text-ledger-sub font-mono">
                        Current status: {stale.status}
                        {stale.expected_completion ? ` · due ${formatDate(stale.expected_completion)}` : ""}
                      </div>
                      <button
                        onClick={() => refreshMatch(m)}
                        data-testid={`reconciliation-refresh-${m.reconciliation_type}`}
                        className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-ledger-border text-xs text-ledger-ink hover:border-ledger-ink"
                      >
                        <RefreshCcw className="h-3.5 w-3.5" /> Refresh commitment
                      </button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-ledger-muted mb-1">Existing commitment</div>
                      <div className="font-sans text-sm text-ledger-ink font-semibold">{existing.promise_text}</div>
                      <div className="text-[12px] text-ledger-sub mt-0.5 font-mono">
                        {existing.promisor_name} · {existing.promisor_organization || "—"}
                      </div>
                      <div className="text-[11px] text-ledger-muted mt-1 font-mono">
                        status: {existing.status}
                        {existing.expected_completion && <> · current deadline {formatDate(existing.expected_completion)}</>}
                        {existing.amount != null && <> · {formatMoney(existing.amount, existing.currency)}</>}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-ledger-muted mb-1">New evidence</div>
                      <blockquote
                        data-testid={`reconciliation-evidence-${m.reconciliation_type}`}
                        className="font-serif italic text-[13px] text-ledger-sub border-l-2 pl-3"
                        style={{ borderColor: meta.border }}
                      >
                        &ldquo;{m.full_evidence || m.evidence_quote}&rdquo;
                      </blockquote>
                      <div className="text-[11px] text-ledger-muted mt-1 font-mono">
                        {m.source_thread_title ? `${m.source_thread_title} · ` : ""}confidence {Math.round(m.confidence * 100)}%
                      </div>

                      {isRevisionLike && (
                        <div className="mt-3 bg-white/80 border rounded-sm p-2.5" style={{ borderColor: meta.border }} data-testid={`revision-detail-${m.reconciliation_type}`}>
                          <div className="grid grid-cols-1 gap-1 font-mono text-[12px]">
                            <div>
                              <span className="text-ledger-muted uppercase tracking-wider text-[10px] mr-1">Current deadline:</span>
                              <span className="text-ledger-ink" data-testid="revision-current-deadline">
                                {pc.previous_expected_completion
                                  ? formatDate(pc.previous_expected_completion)
                                  : existing.expected_completion
                                  ? formatDate(existing.expected_completion)
                                  : pc.original_deadline_phrase || "—"}
                              </span>
                            </div>
                            {pc.withdrawn ? (
                              <div>
                                <span className="text-ledger-muted uppercase tracking-wider text-[10px] mr-1">Proposed:</span>
                                <span className="text-[#92400E]">withdrawn — awaiting a replacement</span>
                              </div>
                            ) : pc.proposed_expected_completion ? (
                              <div>
                                <span className="text-ledger-muted uppercase tracking-wider text-[10px] mr-1">Proposed deadline:</span>
                                <span className="text-ledger-ink" data-testid="revision-proposed-deadline">
                                  {formatDate(pc.proposed_expected_completion)}
                                </span>
                                {pc.revised_deadline_phrase && (
                                  <span className="text-ledger-sub"> — “{pc.revised_deadline_phrase}”</span>
                                )}
                                {pc.deadline_certainty && (
                                  <span className="text-ledger-muted"> — {pc.deadline_certainty}</span>
                                )}
                              </div>
                            ) : pc.revised_deadline_phrase ? (
                              <div>
                                <span className="text-ledger-muted uppercase tracking-wider text-[10px] mr-1">Proposed timeframe:</span>
                                <span className="text-ledger-ink" data-testid="revision-proposed-phrase">
                                  “{pc.revised_deadline_phrase}”
                                </span>
                                <span className="text-ledger-muted"> — {pc.deadline_certainty || "approximate"}</span>
                              </div>
                            ) : (
                              <div className="text-[#92400E]" data-testid="revision-missing-replacement">
                                No proposed field change extracted — cannot confirm. Edit the source or dismiss.
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {m.reconciliation_type === "partially_fulfilled" && (
                        <div className="mt-3 bg-white/80 border rounded-sm p-2.5" style={{ borderColor: meta.border }} data-testid="partial-detail">
                          <div className="grid grid-cols-1 gap-1 font-mono text-[12px]">
                            <div>
                              <span className="text-ledger-muted uppercase tracking-wider text-[10px] mr-1">Original amount:</span>
                              <span className="text-ledger-ink">{formatMoney(existing.original_amount || existing.amount, pc.currency || existing.currency)}</span>
                            </div>
                            <div>
                              <span className="text-ledger-muted uppercase tracking-wider text-[10px] mr-1">Paying now:</span>
                              <span className="text-[#1D4ED8]" data-testid="partial-amount-paid">{formatMoney(pc.amount_paid, pc.currency || existing.currency)}</span>
                            </div>
                            <div>
                              <span className="text-ledger-muted uppercase tracking-wider text-[10px] mr-1">Outstanding after:</span>
                              <span className="text-ledger-ink" data-testid="partial-outstanding">{formatMoney(pc.remaining, pc.currency || existing.currency)}</span>
                            </div>
                            {pc.remaining_deadline_phrase && (
                              <div>
                                <span className="text-ledger-muted uppercase tracking-wider text-[10px] mr-1">Remaining expected:</span>
                                <span className="text-ledger-ink">“{pc.remaining_deadline_phrase}”</span>
                                <span className="text-ledger-muted"> — {pc.remaining_deadline_certainty || "approximate"}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {m.reconciliation_type === "blocked" && pc.blocker && (
                        <div className="text-[12px] text-[#991B1B] font-mono mt-2">
                          blocker: {pc.blocker}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap justify-end">
                    <button
                      onClick={() => onOpenChange(false)}
                      data-testid={`reconciliation-not-${m.reconciliation_type}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-ledger-border text-xs text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink"
                    >
                      Not related
                    </button>
                    <button
                      onClick={() => applyReconciliation(m)}
                      disabled={isRevisionLike && !hasProposedField}
                      data-testid={`reconciliation-confirm-${m.reconciliation_type}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-xs font-medium hover:opacity-90 disabled:opacity-40"
                      style={{ backgroundColor: meta.fg }}
                    >
                      <Check className="h-3.5 w-3.5" />
                      {m.reconciliation_type === "revised" && "Confirm revision"}
                      {m.reconciliation_type === "delayed" && "Confirm delay"}
                      {m.reconciliation_type === "blocked" && "Confirm blocked"}
                      {m.reconciliation_type === "cancelled" && "Confirm cancelled"}
                      {m.reconciliation_type === "partially_fulfilled" && "Confirm partial fulfilment"}
                      {m.reconciliation_type === "condition_satisfied" && "Confirm condition met"}
                      {m.reconciliation_type === "condition_failed" && "Confirm condition failed"}
                      {m.reconciliation_type === "fulfilled" && "Confirm fulfilled"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {fulfillmentMatches.length > 0 && (
          <div className="px-8 py-4 border-b border-ledger-border bg-[#F0FDF4]">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#047857] mb-2 inline-flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" /> Possible fulfilment detected
            </div>
            <div className="space-y-3">
              {fulfillmentMatches.map((m) => (
                <div
                  key={m.candidate_id}
                  data-testid={`fulfilment-match-${m.candidate_id}`}
                  className="bg-white border border-[#10B981] rounded-sm p-4"
                >
                  <div className="text-[11px] uppercase tracking-wider text-ledger-muted mb-1">
                    Existing commitment
                  </div>
                  <div className="font-sans text-sm text-ledger-ink font-semibold">
                    {m.existing_commitment.promise_text}
                  </div>
                  <div className="text-[12px] text-ledger-sub mt-0.5 font-mono">
                    {m.existing_commitment.promisor_name} · expected {formatDate(m.existing_commitment.expected_completion)}
                  </div>
                  <div className="mt-3 text-[11px] uppercase tracking-wider text-ledger-muted mb-1">
                    New evidence
                  </div>
                  <blockquote className="border-l-2 border-[#10B981] bg-[#ECFDF5] pl-3 py-1 font-serif italic text-[13px] text-ledger-sub">
                    &ldquo;{m.evidence_quote}&rdquo;
                  </blockquote>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] text-ledger-muted">
                      Match confidence {Math.round(m.confidence * 100)}% · suggested date {formatDate(m.suggested_fulfilled_date)}
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      <button
                        onClick={() => confirmFulfillment(m)}
                        data-testid={`fulfilment-confirm-${m.candidate_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#047857] text-white text-xs font-medium hover:bg-[#036344]"
                      >
                        <Check className="h-3.5 w-3.5" /> Confirm fulfilled
                      </button>
                      <button
                        onClick={() => {
                          // treat as not a match: add candidate to detected instead
                          setItems((arr) => [...arr, m.candidate]);
                          setSelected((s) => ({ ...s, [m.candidate.id]: true }));
                        }}
                        data-testid={`fulfilment-not-${m.candidate_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-ledger-border text-xs text-ledger-sub hover:border-ledger-ink hover:text-ledger-ink"
                      >
                        Not a match
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {items.length > 0 && (
          <div className="px-8 pt-3 pb-2 flex items-center gap-3">
            <button
              onClick={() => setAll(true)}
              data-testid="review-select-all"
              className="font-mono text-[11px] uppercase tracking-wider text-ledger-sub hover:text-ledger-ink"
            >
              Select all
            </button>
            <span className="text-ledger-muted">·</span>
            <button
              onClick={() => setAll(false)}
              data-testid="review-select-none"
              className="font-mono text-[11px] uppercase tracking-wider text-ledger-sub hover:text-ledger-ink"
            >
              Clear
            </button>
          </div>
        )}

        <div className="px-8 py-4 divide-y divide-ledger-border">
          {items.map((c) => {
            const dupe = dupeByCand[c.id];
            const resolution = duplicateResolutions[c.id];
            return (
              <div
                key={c.id}
                data-testid={`review-item-${c.id}`}
                className={"py-4 flex gap-4 items-start " + (selected[c.id] ? "opacity-100" : "opacity-50")}
              >
                <input
                  type="checkbox"
                  checked={!!selected[c.id]}
                  onChange={() => toggle(c.id)}
                  data-testid={`review-check-${c.id}`}
                  className="mt-1 h-4 w-4 accent-black"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-sans text-sm font-semibold text-ledger-ink">
                      {c.direction === "to_me" ? c.promisor_name : c.promisee_name}
                    </span>
                    {c.promisor_organization && (
                      <span className="text-xs text-ledger-muted font-mono">· {c.promisor_organization}</span>
                    )}
                    <Badge meta={strengthMeta[c.strength] || strengthMeta.strong} />
                    {dupe && (
                      <span
                        data-testid={`dupe-flag-${c.id}`}
                        className="inline-flex items-center gap-1 px-2 py-[3px] font-mono text-[10px] uppercase tracking-wider rounded-sm bg-[#FEF3C7] text-[#92400E]"
                      >
                        <AlertCircle className="h-3 w-3" /> Possible duplicate
                      </span>
                    )}
                    <span className="ml-auto font-mono text-[10px] uppercase text-ledger-muted">
                      {c.direction === "to_me" ? "Promised to me" : "Promised by me"}
                    </span>
                  </div>

                  <input
                    value={c.promise_text}
                    onChange={(e) => editField(c.id, "promise_text", e.target.value)}
                    data-testid={`review-promise-${c.id}`}
                    className="mt-2 w-full bg-transparent border-b border-dashed border-ledger-border pb-1 text-sm text-ledger-ink focus:outline-none focus:border-ledger-ink"
                  />

                  <blockquote className="mt-2 border-l-2 border-ledger-goldBorder bg-ledger-goldBg/40 pl-3 py-2 font-serif italic text-[13px] text-ledger-sub">
                    <span className="inline-flex items-center gap-1 text-[10px] font-sans not-italic text-ledger-muted uppercase tracking-wider mr-1">
                      <Quote className="h-3 w-3" /> Evidence
                    </span>
                    &ldquo;{c.evidence_quote}&rdquo;
                  </blockquote>

                  {c.condition && (
                    <div className="mt-2 text-[12px] text-[#6D28D9] font-mono">
                      <span className="opacity-70">condition:</span> {c.condition}
                    </div>
                  )}

                  <div className="mt-2 flex flex-wrap gap-3 items-center font-mono text-[11px] text-ledger-sub">
                    <label className="flex items-center gap-1">
                      <span className="text-ledger-muted uppercase tracking-wider">Due</span>
                      <input
                        type="date"
                        value={c.expected_completion || ""}
                        onChange={(e) => editField(c.id, "expected_completion", e.target.value || null)}
                        data-testid={`review-date-${c.id}`}
                        className="bg-white border border-ledger-border rounded-sm px-2 py-0.5 text-[11px] font-mono"
                      />
                    </label>
                    {c.timeframe_text && (
                      <span className="inline-flex items-center gap-1 text-ledger-sub">
                        <ShieldQuestion className="h-3 w-3" /> {c.timeframe_text}
                      </span>
                    )}
                    <label className="flex items-center gap-1">
                      <CircleDollarSign className="h-3 w-3 text-[#B45309]" />
                      <input
                        type="number"
                        value={c.amount == null ? "" : c.amount}
                        onChange={(e) => editField(c.id, "amount", e.target.value === "" ? null : parseFloat(e.target.value))}
                        data-testid={`review-amount-${c.id}`}
                        placeholder="amount"
                        className="bg-white border border-ledger-border rounded-sm px-2 py-0.5 text-[11px] font-mono w-24"
                      />
                    </label>
                    <span className="ml-auto">confidence {Math.round((c.confidence || 0) * 100)}%</span>
                  </div>

                  {dupe && (
                    <div className="mt-3 bg-[#FFFBEB] border border-[#F59E0B] rounded-sm p-3" data-testid={`dupe-card-${c.id}`}>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-[#92400E] mb-1">
                        Existing commitment · {dupe.reasons.join(" · ")}
                      </div>
                      <div className="font-sans text-[13px] text-ledger-ink">
                        {dupe.existing_commitment.promise_text}
                      </div>
                      <div className="text-[11px] text-ledger-sub font-mono mt-0.5">
                        {dupe.existing_commitment.promisor_name} · {dupe.existing_commitment.expected_completion ? formatDate(dupe.existing_commitment.expected_completion) : "no due date"}
                      </div>
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {["keep_existing", "replace", "keep_both"].map((k) => (
                          <button
                            key={k}
                            data-testid={`dupe-${k}-${c.id}`}
                            onClick={() => setDuplicateResolutions((r) => ({ ...r, [c.id]: k }))}
                            className={
                              "px-3 py-1 font-mono text-[11px] uppercase tracking-wider rounded-sm border " +
                              (resolution === k
                                ? "bg-ledger-ink text-white border-ledger-ink"
                                : "bg-white text-ledger-sub border-ledger-border hover:border-ledger-ink")
                            }
                          >
                            {k === "keep_existing" ? "Keep existing" : k === "replace" ? "Replace existing" : "Keep both"}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {items.length === 0 && fulfillmentMatches.length === 0 && reconciliationMatches.length === 0 && (
            <div className="py-8 text-center text-sm text-ledger-muted font-mono">
              Nothing to review.
            </div>
          )}

          {rejected.length > 0 && (
            <details className="pt-4">
              <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-wider text-ledger-muted">
                {rejected.length} non-commitment{rejected.length === 1 ? "" : "s"} filtered out
              </summary>
              <ul className="mt-3 space-y-2">
                {rejected.map((r, i) => (
                  <li key={i} className="text-[12px] text-ledger-sub">
                    <span className="font-mono text-[10px] uppercase text-ledger-muted mr-2">
                      {r.kind}
                    </span>
                    &ldquo;{r.text}&rdquo;{" "}
                    <span className="text-ledger-muted">— {r.reason}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        <div className="px-8 py-5 border-t border-ledger-border flex items-center justify-between sticky bottom-0 bg-ledger-surface">
          <button
            onClick={() => {
              if (analysis?.source_thread_id || analysis?.__sourceThreadId) {
                window.dispatchEvent(new CustomEvent("cl:reviewDismissed", { detail: { sourceThreadId: analysis.source_thread_id || analysis.__sourceThreadId } }));
              }
              onOpenChange(false);
            }}
            className="px-4 py-2 text-sm text-ledger-sub hover:text-ledger-ink inline-flex items-center gap-1"
            data-testid="review-cancel"
          >
            <X className="h-4 w-4" /> {items.length === 0 && fulfillmentMatches.length === 0 ? "Close" : "Cancel"}
          </button>
          {items.length > 0 && (
            <button
              onClick={acceptSelected}
              disabled={saving || chosenCount === 0}
              data-testid="review-accept"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ledger-ink text-white text-sm font-medium hover:bg-neutral-800 disabled:opacity-40 transition-colors duration-200"
            >
              <Check className="h-4 w-4" />
              {saving ? "Adding…" : `Accept ${chosenCount} to ledger`}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
